import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { BoardContactService } from '../../../../services/board-contact.service';
import { BoardContact } from '../../board-v3.types';
import { formatContactPhone, getContactEmails, getContactName, getContactPhones } from '../contact.utils';

export type LinkContactResult = { contact: BoardContact } | { create: true };

@Component({
  selector: 'app-board-v3-link-contact',
  templateUrl: './link-contact.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full min-h-0' },
})
export class BoardV3LinkContactComponent implements OnInit {
  /** Contacts that are already linked and should not be offered */
  @Input() excludes: string[] = [];
  @Output() close = new EventEmitter<LinkContactResult | void>();

  private readonly contactService = inject(BoardContactService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchChanged = new Subject<void>();

  protected readonly take = 25;
  protected contacts: BoardContact[] = [];
  protected search = '';
  protected loading = false;
  protected loadingMore = false;

  protected readonly getContactName = getContactName;
  protected readonly formatContactPhone = formatContactPhone;
  protected readonly getContactPhones = getContactPhones;
  protected readonly getContactEmails = getContactEmails;

  private page = 1;
  private total = 0;
  private request?: Subscription;

  ngOnInit(): void {
    this.searchChanged.pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.reload());
    this.reload();
  }

  protected isExcluded(contact: BoardContact): boolean {
    return this.excludes.includes(contact.id);
  }

  protected onSearchChange(value: string): void {
    this.search = value;
    this.searchChanged.next();
  }

  protected onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.scrollHeight - target.scrollTop - target.clientHeight > 200) return;

    this.loadMore();
  }

  protected onSelect(contact: BoardContact): void {
    if (this.isExcluded(contact)) return;

    this.close.emit({ contact });
  }

  protected onCreate(): void {
    this.close.emit({ create: true });
  }

  protected onCancel(): void {
    this.close.emit();
  }

  private reload(): void {
    this.page = 1;
    this.loading = true;
    this.loadContacts(true);
  }

  private loadMore(): void {
    if (this.loading || this.loadingMore || this.contacts.length >= this.total) return;

    this.page += 1;
    this.loadingMore = true;
    this.loadContacts(false);
  }

  private loadContacts(reset: boolean): void {
    this.request?.unsubscribe();
    this.request = this.contactService
      .getContacts({ search: this.search, page: this.page, take: this.take })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.contacts = reset ? response.items : [...this.contacts, ...response.items];
          this.total = response.total;
          this.loading = false;
          this.loadingMore = false;
        },
        error: () => {
          if (!reset) this.page -= 1;
          this.loading = false;
          this.loadingMore = false;
        },
      });
  }
}
